import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  BackButton,
  ContentInner,
  MainHeading,
  PageWrap,
  PaymentActions,
} from "./styles";
import cache from "utils/cache";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useConfirmStripeCheckoutSessionMutation,
  useCreateStripeCheckoutSessionMutation,
} from "api/orderApi";
import { loadStripe } from "@stripe/stripe-js";
import Loader from "shared/Loader";
import Layout from "layout";
import {
  formatTaxRate,
  splitTotalIntoSubtotalAndTax,
} from "utils/checkoutPricing";
import { useCartDrawer } from "context/CartDrawerContext";

const MARKETPLACE_LABELS = {
  SHOPIFY: "Shopify",
  EBAY: "eBay",
  AMAZON: "Amazon",
  TIKTOK: "TikTok",
  WALMART: "Walmart",
};

const getMarketplaceLabel = (marketplace) => {
  const key = (marketplace || "").toString().toUpperCase().replace(/\s+/g, "");
  return MARKETPLACE_LABELS[key] || null;
};

const OrderPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCartDrawer();
  const [createStripeCheckoutSession, { isLoading: isCreatingStripeSession }] =
    useCreateStripeCheckoutSessionMutation();
  const [confirmStripeCheckoutSession] = useConfirmStripeCheckoutSessionMutation();
  const isConfirmingStripeRef = useRef(false);
  const cachedItems = useMemo(() => cache.getItem("cartItems") || [], []);
  const stripeReturnParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const {
    totalAmount,
    taxRate,
    marketplace = "SHOPIFY",
    existingOrderId: stateExistingOrderId,
  } =
    location.state || {};
  const existingOrderId =
    stateExistingOrderId || stripeReturnParams.get("existing_order_id") || null;
  const isExistingOrderPayment = Boolean(existingOrderId);
  const existingOrderPricing =
    isExistingOrderPayment && totalAmount != null && totalAmount !== ""
      ? splitTotalIntoSubtotalAndTax(totalAmount, taxRate)
      : null;
  const marketplaceLabel = getMarketplaceLabel(marketplace);
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  const isStripeReturnFlow =
    stripeReturnParams.get("status") === "success" &&
    stripeReturnParams.get("gateway") === "STRIPE" &&
    !!stripeReturnParams.get("session_id");
  const [isStripeRedirectLoading, setIsStripeRedirectLoading] =
    useState(isStripeReturnFlow);

  const mapOrderItems = useCallback(
    () =>
      cachedItems.map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.productQuantity,
        price:
          item?.variantPrice && item.variantPrice !== 0
            ? Number(item.variantPrice)
            : Number(item.productPrice),
      })),
    [cachedItems]
  );

  const finalizePaidOrder = useCallback(
    (orderId) => {
      clearCart();
      message.success("Payment completed. Your order is now processing.");
      navigate(orderId ? "/my-orders" : "/success", { replace: true });
    },
    [clearCart, navigate]
  );

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(isExistingOrderPayment ? "/my-orders" : "/checkout");
  };

  const handleStripeCheckout = async () => {
    if (!isExistingOrderPayment && !cachedItems.length) {
      message.error("Your cart is empty");
      return;
    }

    const orderItems = isExistingOrderPayment ? undefined : mapOrderItems();
    const response = await createStripeCheckoutSession({
      totalAmount: Number(totalAmount),
      gateway: "STRIPE",
      connectFlow: true,
      marketplace,
      ...(orderItems ? { items: orderItems } : {}),
      ...(existingOrderId ? { existingOrderId } : {}),
    });
    if (response?.error) {
      message.error(response?.error?.data?.message || "Unable to create Stripe checkout");
      return;
    }

    const checkoutUrl = response.data?.link;
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
      return;
    }

    const stripe = await stripePromise;
    if (stripe && response.data?.sessionId) {
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId,
      });
      if (error) {
        message.error(error.message || "Redirect to Stripe failed");
      }
      return;
    }

    message.error("Stripe checkout link not received. Please try again.");
  };

  useEffect(() => {
    const confirmStripePayment = async () => {
      const status = stripeReturnParams.get("status");
      const sessionId = stripeReturnParams.get("session_id");
      const gateway = stripeReturnParams.get("gateway");

      if (status !== "success" || gateway !== "STRIPE" || !sessionId) {
        setIsStripeRedirectLoading(false);
        return;
      }
      if (isConfirmingStripeRef.current) {
        return;
      }

      const sessionStorageKey = `stripe-checkout-confirmed-${sessionId}`;
      if (sessionStorage.getItem(sessionStorageKey) === "done") {
        const completedOrderId =
          stripeReturnParams.get("existing_order_id") || existingOrderId;
        clearCart();
        navigate(completedOrderId ? "/my-orders" : "/success", { replace: true });
        return;
      }
      if (sessionStorage.getItem(sessionStorageKey) === "processing") {
        return;
      }

      isConfirmingStripeRef.current = true;
      setIsStripeRedirectLoading(true);
      sessionStorage.setItem(sessionStorageKey, "processing");

      const response = await confirmStripeCheckoutSession({ sessionId });
      if (response?.error) {
        sessionStorage.removeItem(sessionStorageKey);
        isConfirmingStripeRef.current = false;
        setIsStripeRedirectLoading(false);
        message.error(response?.error?.data?.message || "Unable to confirm Stripe payment");
        return;
      }

      // Mark done only after backend has finalized the order (or webhook already did).
      sessionStorage.setItem(sessionStorageKey, "done");
      const completedOrderId =
        response.data?.existingOrderId ||
        stripeReturnParams.get("existing_order_id") ||
        existingOrderId;
      finalizePaidOrder(completedOrderId);
      isConfirmingStripeRef.current = false;

      const cleanUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, cleanUrl);
    };

    confirmStripePayment();
  }, [
    confirmStripeCheckoutSession,
    stripeReturnParams,
    navigate,
    existingOrderId,
    finalizePaidOrder,
    clearCart,
  ]);

  if (isStripeRedirectLoading) {
    return (
      <Layout>
        <Loader isLoading={true} />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageWrap>
        <ContentInner>
          <BackButton type="button" onClick={handleBack}>
            <ArrowLeftOutlined />
            Back
          </BackButton>
          <MainHeading>
            {isExistingOrderPayment ? "Complete Order Payment" : "Enter Payment Details"}
          </MainHeading>
          {isExistingOrderPayment && existingOrderPricing && (
            <div style={{ marginBottom: 16, color: "#64748b" }}>
              <p style={{ marginBottom: 8 }}>
                {marketplaceLabel
                  ? `Pay to start processing this ${marketplaceLabel} order using our catalog pricing.`
                  : "Pay to start processing this order using our catalog pricing."}
              </p>
              <p>Items: ${existingOrderPricing.subtotal.toFixed(2)}</p>
              <p>Tax ({formatTaxRate(taxRate)}): ${existingOrderPricing.tax.toFixed(2)}</p>
              <p style={{ fontWeight: 600, color: "#0f172a" }}>
                Total: ${existingOrderPricing.total.toFixed(2)}
              </p>
            </div>
          )}
          <PaymentActions>
            <Button
              type="primary"
              block
              onClick={handleStripeCheckout}
              loading={isCreatingStripeSession}
            >
              {`Pay with Stripe`}
            </Button>
          </PaymentActions>
        </ContentInner>
      </PageWrap>
    </Layout>
  );
};

export default OrderPayment;
